import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext, useWatch } from 'react-hook-form'
import { PlusIcon, CalendarIcon } from '@untitledui-icons/react/line'
import { Button } from '@/components/base/buttons'
import { EmptyState } from '@/components/common/EmptyState'
import { usePhoneNumber } from '@/hooks/api'
import { WorkingHoursCard, type WorkingHoursRule } from './WorkingHoursCard'
import { WorkingHoursModal } from './WorkingHoursModal'

export function WorkingHoursSection() {
  const { t } = useTranslation()
  const { setValue, getValues, watch, control } = useFormContext()
  const [workingHoursRules, setWorkingHoursRules] = useState<WorkingHoursRule[]>([])
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [tempRule, setTempRule] = useState<WorkingHoursRule | null>(null)
  
  const config = watch('config')

  // Resolve the assistant's phone number for the caller ID option
  const phoneNumberId = useWatch({ control, name: 'phone_number_id' })
  const { data: phoneNumberData } = usePhoneNumber(phoneNumberId)

  // Load existing rules when component mounts or config changes
  useEffect(() => {
    const configData = getValues('config') || {}
    const rules = configData.working_hours || []
    setWorkingHoursRules(rules)
  }, [getValues, config])

  const handleAddRule = () => {
    setTempRule(null)
    setEditingIndex(-1)
  }

  const handleEditRule = (index: number) => {
    setTempRule(workingHoursRules[index])
    setEditingIndex(index)
  }

  const handleDeleteRule = (index: number) => {
    const updatedRules = workingHoursRules.filter((_, i) => i !== index)
    setWorkingHoursRules(updatedRules)
    
    const currentConfig = getValues('config') || {}
    const newConfig = {
      ...currentConfig,
      working_hours: updatedRules
    }
    
    setValue('config', newConfig, { shouldDirty: true, shouldTouch: true })
  }

  const handleSaveRule = (rule: WorkingHoursRule, index: number) => {
    let updatedRules: WorkingHoursRule[]
    
    if (index >= 0) {
      updatedRules = [...workingHoursRules]
      updatedRules[index] = rule
    } else {
      updatedRules = [...workingHoursRules, rule]
    }
    
    setWorkingHoursRules(updatedRules)
    
    const currentConfig = getValues('config') || {}
    const newConfig = {
      ...currentConfig,
      working_hours: updatedRules
    }
    
    setValue('config', newConfig, { shouldDirty: true, shouldTouch: true })
    setEditingIndex(null)
    setTempRule(null)
  }

  const handleCloseModal = () => {
    setEditingIndex(null)
    setTempRule(null)
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {t('resources.assistants.answer_schedule.title')}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('resources.assistants.answer_schedule.description')}
          </p>
        </div>
        <Button color="secondary" size="sm" onPress={handleAddRule} iconLeading={PlusIcon}>
          {t('resources.assistants.answer_schedule.add')}
        </Button>
      </div>

      {workingHoursRules.length === 0 ? (
        <EmptyState
          variant="card"
          icon={<CalendarIcon className="h-12 w-12 text-gray-400 dark:text-gray-500" />}
          title={t('resources.assistants.answer_schedule.empty_title')}
          description={t('resources.assistants.answer_schedule.empty_description')}
          action={{
            label: t('resources.assistants.answer_schedule.setup'),
            onClick: handleAddRule
          }}
        />
      ) : (
        <div className="space-y-2">
          {workingHoursRules.map((rule, index) => (
            <WorkingHoursCard
              key={index}
              rule={rule}
              index={index}
              onEdit={() => handleEditRule(index)}
              onDelete={() => handleDeleteRule(index)}
            />
          ))}
        </div>
      )}

      {/* Modal */}
      <WorkingHoursModal
        isOpen={editingIndex !== null}
        onClose={handleCloseModal}
        rule={tempRule}
        index={editingIndex ?? -1}
        onSave={handleSaveRule}
        assistantPhoneNumber={phoneNumberData?.number}
      />
    </div>
  )
}

export default WorkingHoursSection
